
import { Component, Vue } from 'vue-property-decorator';
import { getDataServerUrl } from './utils/functions';

import {
  Campaign,
  SiteStatus,
  LoginInfo,
  LoginProvider,
  SiteInfo,
  User,
  UserProperties,
  zawaroom,
} from './utils/models';
import LoginPopup from '@/components/popup/common/LoginPopup.vue';

import { initMediaServerUrl } from '@/service/JitsiLibWrapper';
import { DataService } from './service/DataService';

import { version } from '../package.json';
import { buildVersion } from '../build-version.json';

import store from 'store';
import CampaignsPopup from './components/popup/common/CampaignsPopup.vue';
import { v4 as uuid } from 'uuid';

@Component({ components: { LoginPopup, CampaignsPopup } })
export default class App extends Vue {
  private servUrl = getDataServerUrl();
  isLogin = false;
  isShowLoginPopup = false;

  get user(): User {
    return this.$store.state.user;
  }
  private get isAdmin(): boolean {
    return !!this.user?.roles?.find((r) => r.id === 'Admin');
  }

  get isTrialVersion() {
    return !!this.$store.state.siteInfo?.isDemo;
  }

  get campaignsVisible() {
    return this.isTrialVersion && this.$store.state.campaigns.length > 0;
  }

  get campaingsClasses() {
    const isRoomView = this.$route.name === 'RoomViewWithId';
    return ['campaigns', isRoomView ? 'room' : 'home'];
  }

  async created() {
    const versioninfo = `v ${version} (build ${buildVersion})`;
    console.log('version', versioninfo);
    this.$store.commit('setVersion', versioninfo);
    window.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.shiftKey && ev.ctrlKey && ev.key === 'Shift' && (ev.code === 'ShiftRight' || ev.code === '')) {
        this.$store.commit('ToggleDebugMode');
        console.log('change debug mode', this.$store.state.isDebugMode);
      } else if (ev.shiftKey && ev.altKey && this.isAdmin && ev.code === 'KeyA') {
        this.$router.push('/admin');
      }
    });
    window.addEventListener('beforeunload', () => {
      zawaroom.dataService?.disconnect();
      zawaroom.dataService = null;
    });
    await this.loadConfig();
  }
  beforeDestroy() {
    if (this.$store.state.dataService) {
      let h: DataService = this.$store.state.dataService;
      h.disconnect();
    }
  }
  async loadConfig() {
    const c = await this.axios.get<SiteInfo>(this.servUrl + '/zawa-data/siteinfo');
    this.$store.commit('SetSiteInfo', c.data);
    const promises = [
      c.data.defaultMediaServerUrl ? initMediaServerUrl(c.data.defaultMediaServerUrl) : Promise.resolve(),
      this.axios.get<LoginProvider[]>(this.servUrl + '/zawa-data/providers').then((p) => {
        this.$store.commit('SetProviders', p.data);
      }),
    ];
    promises.push(
      this.axios.get<SiteStatus>(this.servUrl + '/zawa-data/site-status').then((status) => {
        this.$store.commit('setSiteStatus', status.data);
      }),
    );
    await Promise.all(promises);
    this.isShowLoginPopup = true;
  }
  async onAuthrized(loginInfo: LoginInfo) {
    const dataService = new DataService(getDataServerUrl(), loginInfo, version, this.$store.state.version);
    dataService.onReconnectFailed((message) => {
      this.isLogin = false;

      this.axios
        .post(this.servUrl + '/zawa-data/api/log', {
          message,
          userId: this.user.id,
        })
        .catch(() => {
          console.log('ログを送信できませんでした。', message);

          localStorage.setItem('errorlog_' + Date.now(), message);
        });
      this.$nextTick().then(() => {
        zawaroom.dataService = null;
        this.$store.commit('SetDataService', null);
        this.$store.commit('SetUser', null);
        dataService.disconnect();
        this.isShowLoginPopup = true;
        //MagicLinkによるログイン試行失敗時はリロードしない。（リロードしてもトークンは変わらないのにも関わらず自動でログイン試行がまた始まり失敗するというループに入るため。）
        if (loginInfo.loginWithMagicLink) {
          this.$toast.error(`Magic Linkログインに失敗しました。${message}`);
        } else {
          location.reload();
        }
      });
    });
    zawaroom.dataService = dataService;
    this.$store.commit('SetDataService', dataService);

    zawaroom.dataService.onSiteStatusChange((siteStatus, senderId?: string) => {
      this.$store.commit('setSiteStatus', siteStatus);
      const suspensionStatuses = new Set<SiteStatus['status']>([
        'zawa-suspended',
        'zawademo-suspended',
        'zawademo-scheduled-suspension',
      ]);
      //サイト一時停止時はログアウト。
      if (suspensionStatuses.has(siteStatus.status) && zawaroom.dataService?.socketId !== senderId) {
        this.isLogin = false;
        this.isShowLoginPopup = true;
        zawaroom.dataService = null;
      }
    });
    if (this.isTrialVersion) {
      zawaroom.dataService.emitGetCampaigns().then((campaigns) => {
        this.$store.commit('setCampaigns', campaigns);
      });
      zawaroom.dataService.onCampaignCreated((campaign: Campaign) => {
        this.$store.commit('addCampaign', campaign);
      });

      zawaroom.dataService.onCampaignsUpdated((campaigns) => {
        this.$store.commit('setCampaigns', campaigns);
      });

      zawaroom.dataService.onCampaignsDeleted((campaigns) => {
        this.$store.commit('deleteCampaigns', campaigns);
      });

      zawaroom.dataService.onSiteInfoUpdated((siteInfo) => {
        this.$store.commit('SetSiteInfo', siteInfo);
      });
    }

    dataService.onceConnected(() => {
      dataService.onVersionInvalid((v) => {
        this.$toast.error('最新バージョンが公開されています。ブラウザをリロードして最新バージョンをご利用ください。', {
          onClick: () => location.reload(),
          onClose: () => location.reload(),
          timeout: false,
        });
      });
      const keys = Object.keys(localStorage).filter((k) => k.startsWith('errorlog_'));
      if (keys.length) {
        this.axios
          .post(this.servUrl + '/zawa-data/api/log', {
            message: keys.map((k) => k.replace('errorlog_', '') + ':' + localStorage.getItem(k)).join(','),
            userId: loginInfo.id,
          })
          .then(() => {
            keys.forEach((k) => localStorage.removeItem(k));
          });
      }
      dataService.emitLogin().then((u) => {
        console.log('login success', u);
        if (typeof u === 'object' && 'user' in u && 'properties' in u) {
          this.$store.commit('SetUser', u.user);
          this.$store.commit('SetUserProperties', u.properties);

          // migration 処理
          if (store.get('lastSearchType') || store.get('lastSelectedSpaceId')) {
            let props: Partial<UserProperties> = {
              userId: u.user.id,
              lastSelectedMenuCategory: store.get('lastSearchType'),
              lastSelectedSpaceId: store.get('lastSelectedSpaceId'),
              lastSelectedRoomId: store.get('lastSelectedRoomId'),
            };

            if (store.get('selectedNotificationMode')) {
              props.selectedNotificationMode = store.get('selectedNotificationMode');
            }
            if (store.get('selectedProximityNotificationMode')) {
              props.selectedProximityNotificationMode = store.get('selectedProximityNotificationMode');
            }
            if (store.get('selectedMessageNotificationMode')) {
              props.selectedMessageNotificationMode = store.get('selectedMessageNotificationMode');
            }
            if (store.get('enableNoiseSuppressor') === false) {
              props.enableNoiseSuppressor = false;
            }
            if (store.get('enableShortcut') === true) {
              props.enableShortcut = true;
            }
            store.remove('lastSearchType');
            store.remove('lastSelectedSpaceId');
            store.remove('lastSelectedRoomId');
            store.remove('audioDisabledOnInit');
            store.remove('videoDisabledOnInit');
            store.remove('selectedNotificationMode');
            store.remove('selectedProximityNotificationMode');
            store.remove('selectedMessageNotificationMode');
            store.remove('enableNoiseSuppressor');
            store.remove('enableShortcut');
            this.$store.commit('SetUserProperties', {
              ...this.$store.state.userProperties,
              ...props,
            });
            dataService.emitUpdateUserProperties(props).then(() => {
              this.isLogin = true;
              this.isShowLoginPopup = false;
            });
          } else {
            this.isLogin = true;
            this.isShowLoginPopup = false;
          }
        } else {
          console.log('失敗');
          const errorMessage =
            u ?? 'ログインに失敗しました。利用者登録がされていない可能性があります。管理者に問い合わせてください。';
          this.$toast.error(errorMessage);
          dataService.disconnect();
          zawaroom.dataService = null;
          this.$store.commit('SetDataService', null);
        }
      });
      dataService.onErrorFeedback((message) => {
        this.$toast.error(message);
      });
    });
    /// app 共通データに関するhook はここで登録します。
    dataService.onUpdatedUserProperties((p) => this.$store.commit('SetUserProperties', p));
  }
  onCampaignClose(campaign: Campaign) {
    // campaign.closedByUser = true;
    this.$store.commit('setCampaign', campaign);
  }
}
